import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  type ChartOptions,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
  Card,
  CardBody,
  CardHeader,
  CardRow,
  ChartFrame,
  DataTable,
  EmptyState,
  ErrorState,
  PageHeader,
  ProgressBar,
  SegmentedControl,
  Skeleton,
  StatCard,
  formatMoney,
  formatMoneyCompact,
  type DataTableColumn,
} from '@solid/ui';
import { useAuthStore } from '@/stores/auth.store';
import {
  usePipelineSummary,
  useRecentActivity,
  useUpcomingCloses,
  useWinLossStats,
  useFunnel,
  useClosedRevenue,
} from '@/hooks/useDashboard';
import { usePipelines } from '@/hooks/usePipelines';
import { dashboardApi } from '@/lib/api';
import { Avatar } from '@/components/ui/Avatar';
import type { Activity, ClosedRevenueStats, Deal, PipelineMetric, PipelineSummary } from '@/types';

// ---------------------------------------------------------------------------
// Chart.js — registered once, styled once. Every chart on this screen draws
// from the same two series colours (navy = the figure, accent = its companion)
// and the same grey axes, so a reader who learns one chart has learned them all.
// ---------------------------------------------------------------------------

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Filler);
ChartJS.defaults.font.family = 'Mulish, system-ui, sans-serif';
ChartJS.defaults.font.size = 11;
ChartJS.defaults.color = '#9898A5';

const SERIES = { primary: '#142033', secondary: '#2563eb', secondaryFill: 'rgba(37, 99, 235, 0.12)' };
const AXIS = {
  x: { grid: { display: false }, border: { display: false } },
  y: { grid: { color: '#eaeaea' }, border: { display: false } },
} as const;

const money = (v: number) => formatMoney(v, { whole: true });

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.name?.split(' ')[0];

  const summary = usePipelineSummary();
  const winLoss = useWinLossStats();
  const funnel = useFunnel();
  const closed = useClosedRevenue();
  const activity = useRecentActivity(50);
  const upcoming = useUpcomingCloses(10);

  const openDeals = summary.data?.reduce((n: number, s: PipelineSummary) => n + s.dealCount, 0) ?? 0;
  const pipelineValue = summary.data?.reduce((n: number, s: PipelineSummary) => n + s.totalValue, 0) ?? 0;
  const wonThisMonth = closed.data?.thisMonth;

  return (
    <div>
      <PageHeader
        title={firstName ? `Welcome, ${firstName}` : 'Dashboard'}
        subtitle="Open deals, money in play, and what closed."
      />

      {/* Tiles — each is its own query, so each carries its own loading / error. */}
      <CardRow cols={4} className="mb-4 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          kind="deals"
          label="Open deals"
          value={openDeals}
          hint="Across every pipeline"
          to="/admin/deals"
          loading={summary.isLoading}
          error={summary.isError}
          errorMessage={errorText(summary.error)}
          onRetry={() => void summary.refetch()}
        />
        <StatCard
          kind="pipeline"
          label="Pipeline value"
          value={money(pipelineValue)}
          hint="Total of open deals, not weighted"
          loading={summary.isLoading}
          error={summary.isError}
          errorMessage={errorText(summary.error)}
          onRetry={() => void summary.refetch()}
        />
        <StatCard
          kind="won"
          label="Won this month"
          value={wonThisMonth?.dealCount ?? 0}
          hint={wonThisMonth ? `${money(wonThisMonth.value)} closed` : undefined}
          loading={closed.isLoading}
          error={closed.isError}
          errorMessage={errorText(closed.error)}
          onRetry={() => void closed.refetch()}
        />
        <StatCard
          kind="leads"
          label="Contacts"
          value={funnel.data?.contacts ?? 0}
          hint={
            winLoss.data
              ? `${Math.round(winLoss.data.winRate)}% win rate, all time`
              : 'Everyone in the CRM'
          }
          to="/admin/contacts"
          loading={funnel.isLoading}
          error={funnel.isError}
          errorMessage={errorText(funnel.error)}
          onRetry={() => void funnel.refetch()}
        />
      </CardRow>

      <div className="space-y-4">
        <CardRow cols={3}>
          <PipelineCard className="xl:col-span-2" />
          <ClosedBusinessCard
            stats={closed.data}
            loading={closed.isLoading}
            error={closed.isError ? errorText(closed.error) : undefined}
            onRetry={() => void closed.refetch()}
          />
        </CardRow>

        <CardRow cols={3}>
          <Card className="xl:col-span-2">
            <CardHeader title="Upcoming deals" subtitle="Due in the next 30 days" />
            <CardBody>
              {upcoming.isError ? (
                <ErrorState message={errorText(upcoming.error)} onRetry={() => void upcoming.refetch()} />
              ) : upcoming.isLoading ? (
                <div className="space-y-3" aria-busy>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (
                <UpcomingDealsTable deals={upcoming.data ?? []} />
              )}
            </CardBody>
          </Card>
          <ActivityTrendCard
            activities={activity.data}
            loading={activity.isLoading}
            error={activity.isError ? errorText(activity.error) : undefined}
            onRetry={() => void activity.refetch()}
          />
        </CardRow>
      </div>
    </div>
  );
}

function errorText(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined;
}

// ---------------------------------------------------------------------------
// Pipeline — ONE card, ONE pipeline picker, two views of the same stages.
// The old screen auto-cycled two separate cards through the pipelines every
// 8s; a chart that changes under the reader is a performance, not a figure.
// ---------------------------------------------------------------------------

function PipelineCard({ className }: { className?: string }) {
  const pipelines = usePipelines();
  const [selected, setSelected] = useState<string | null>(null);

  const metrics = useQueries({
    queries: (pipelines.data ?? []).map((p) => ({
      queryKey: ['dashboard', 'pipeline-metrics', p.id],
      queryFn: () => dashboardApi.getPipelineMetrics(p.id),
      staleTime: 60_000,
    })),
  });

  const list = pipelines.data ?? [];
  const currentId = selected && list.some((p) => p.id === selected) ? selected : list[0]?.id;
  const currentIndex = list.findIndex((p) => p.id === currentId);
  const current = currentIndex >= 0 ? metrics[currentIndex] : undefined;

  const stages = useMemo(
    () =>
      (current?.data ?? [])
        .filter((m: PipelineMetric) => !m.isTerminal)
        .sort((a: PipelineMetric, b: PipelineMetric) => a.position - b.position),
    [current?.data],
  );

  const loading = pipelines.isLoading || (current?.isLoading ?? false);
  const error = pipelines.isError
    ? errorText(pipelines.error)
    : current?.isError
      ? errorText(current.error)
      : undefined;
  const retry = () => {
    if (pipelines.isError) void pipelines.refetch();
    else void current?.refetch();
  };

  return (
    <Card className={className}>
      <CardHeader
        title="Pipeline"
        subtitle="Open deals by stage — how many, and what they are worth"
        /* The picker wraps under the title at phone widths instead of
           squeezing it into a one-word column. */
        className="flex-wrap"
        actions={
          list.length > 1 && currentId ? (
            <SegmentedControl
              size="sm"
              ariaLabel="Pipeline"
              value={currentId}
              onChange={setSelected}
              options={list.map((p) => ({ value: p.id, label: p.name }))}
              scrollable
            />
          ) : undefined
        }
      />
      <CardBody fill>
        {error ? (
          <ErrorState message={error} onRetry={retry} />
        ) : loading ? (
          <div className="grid gap-4 md:grid-cols-2" aria-busy>
            <Skeleton className="h-52 w-full" />
            <Skeleton className="h-52 w-full" />
          </div>
        ) : stages.length === 0 ? (
          <EmptyState title="No open deals in this pipeline" description="Deals appear here as they enter a stage." />
        ) : (
          <div className="grid flex-1 gap-6 md:grid-cols-2">
            <StageChart title="Deals per stage" stages={stages} mode="count" />
            <StageChart title="Value per stage" stages={stages} mode="value" />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function StageChart({
  title,
  stages,
  mode,
}: {
  title: string;
  stages: PipelineMetric[];
  mode: 'count' | 'value';
}) {
  const data = useMemo(() => {
    const labels = stages.map((s) => s.stageName);
    if (mode === 'count') {
      return {
        labels,
        datasets: [
          {
            label: 'Deals',
            data: stages.map((s) => s.count),
            backgroundColor: SERIES.primary,
            borderRadius: 4,
            barPercentage: 0.6,
          },
        ],
      };
    }
    return {
      labels,
      datasets: [
        {
          label: 'Total',
          data: stages.map((s) => s.totalValue),
          backgroundColor: SERIES.primary,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
        {
          label: 'Weighted',
          data: stages.map((s) => Math.round(s.totalValue * (s.winProbability / 100))),
          backgroundColor: SERIES.secondary,
          borderRadius: 4,
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
      ],
    };
  }, [stages, mode]);

  // An all-zero series would otherwise draw a $0…$1 axis in tenths.
  const peak = useMemo(
    () => Math.max(0, ...stages.map((s) => (mode === 'value' ? s.totalValue : s.count))),
    [stages, mode],
  );

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      plugins: {
        tooltip: {
          callbacks: {
            label: (ctx) =>
              mode === 'value'
                ? `${ctx.dataset.label}: ${money(ctx.parsed.y ?? 0)}`
                : `${ctx.parsed.y ?? 0} ${ctx.parsed.y === 1 ? 'deal' : 'deals'}`,
          },
        },
      },
      scales: {
        x: { ...AXIS.x, ticks: { maxRotation: 0, autoSkip: true } },
        y: {
          ...AXIS.y,
          beginAtZero: true,
          suggestedMax: peak === 0 ? (mode === 'value' ? 1000 : 4) : undefined,
          ticks:
            mode === 'value'
              ? { callback: (v) => formatMoneyCompact(Number(v)), maxTicksLimit: 6 }
              : { stepSize: 1, precision: 0 },
        },
      },
    }),
    [mode, peak],
  );

  return (
    <div className="flex min-h-0 flex-col">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-body-sm font-semibold text-secondary">{title}</p>
        {mode === 'value' && (
          <ul className="flex items-center gap-3 text-caption text-grey-500" aria-label="Legend">
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: SERIES.primary }} aria-hidden />
              Total
            </li>
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: SERIES.secondary }} aria-hidden />
              Weighted by win probability
            </li>
          </ul>
        )}
      </div>
      <ChartFrame min="sm">
        <Bar data={data} options={options} />
      </ChartFrame>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Closed business — won value per period, as a list (not five more tiles).
// ---------------------------------------------------------------------------

const CLOSED_PERIODS: Array<{ key: keyof ClosedRevenueStats; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'thisWeek', label: 'This week' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'thisQuarter', label: 'This quarter' },
  { key: 'thisYear', label: 'This year' },
];

function ClosedBusinessCard({
  stats,
  loading,
  error,
  onRetry,
}: {
  stats?: ClosedRevenueStats;
  loading: boolean;
  error?: string;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardHeader title="Closed business" subtitle="Value of deals won, by period" />
      <CardBody fill>
        {error ? (
          <ErrorState message={error} onRetry={onRetry} />
        ) : loading || !stats ? (
          <div className="space-y-3" aria-busy>
            {CLOSED_PERIODS.map((p) => (
              <Skeleton key={p.key} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <ul className="flex flex-1 flex-col justify-between divide-y divide-grey-100">
            {CLOSED_PERIODS.map(({ key, label }) => {
              const period = stats[key];
              return (
                <li key={key} className="flex items-baseline justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-body-sm font-semibold text-secondary">{label}</p>
                    <p className="text-caption text-grey-500">
                      <span className="num">{period.dealCount}</span> {period.dealCount === 1 ? 'deal' : 'deals'} won
                    </p>
                  </div>
                  <p className="num text-heading-4 text-secondary">{money(period.value)}</p>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Activity trend — activities per day, last 7 days
// ---------------------------------------------------------------------------

function ActivityTrendCard({
  activities,
  loading,
  error,
  onRetry,
}: {
  activities?: Activity[];
  loading: boolean;
  error?: string;
  onRetry: () => void;
}) {
  const { labels, counts } = useMemo(() => {
    const now = new Date();
    const days: string[] = [];
    const dayMap = new Map<string, number>();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push(key);
      dayMap.set(key, 0);
    }
    (activities ?? []).forEach((a) => {
      const dayKey = new Date(a.createdAt).toISOString().slice(0, 10);
      if (dayMap.has(dayKey)) dayMap.set(dayKey, (dayMap.get(dayKey) ?? 0) + 1);
    });
    return {
      labels: days.map((d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short' })),
      counts: days.map((d) => dayMap.get(d) ?? 0),
    };
  }, [activities]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Activities',
          data: counts,
          borderColor: SERIES.secondary,
          backgroundColor: SERIES.secondaryFill,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointBackgroundColor: '#fff',
          pointBorderColor: SERIES.secondary,
          borderWidth: 2,
        },
      ],
    }),
    [labels, counts],
  );

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      plugins: { tooltip: { enabled: true } },
      scales: {
        x: AXIS.x,
        y: { ...AXIS.y, beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
      },
    }),
    [],
  );

  const total = counts.reduce((n, c) => n + c, 0);

  return (
    <Card>
      <CardHeader
        title="Activity"
        subtitle={loading || error ? 'Last 7 days' : `${total} ${total === 1 ? 'activity' : 'activities'} in the last 7 days`}
      />
      <CardBody fill>
        {error ? (
          <ErrorState message={error} onRetry={onRetry} />
        ) : loading ? (
          <Skeleton className="h-52 w-full" aria-busy />
        ) : (
          <ChartFrame min="sm">
            <Line data={data} options={options} />
          </ChartFrame>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Upcoming deals — rows are links, value is a num-cell, stage carries its odds
// ---------------------------------------------------------------------------

const UPCOMING_COLUMNS: Array<DataTableColumn<Deal>> = [
  {
    key: 'deal',
    header: 'Deal',
    render: (deal) => (
      <div className="min-w-0">
        <p className="truncate font-medium text-secondary">{deal.title}</p>
        {deal.organization && <p className="truncate text-caption text-grey-500">{deal.organization.name}</p>}
      </div>
    ),
  },
  {
    key: 'owner',
    header: 'Owner',
    render: (deal) => (
      <div className="flex items-center gap-2">
        <Avatar src={deal.owner.avatarUrl} name={deal.owner.name} size="sm" />
        <span className="hidden text-grey-600 sm:inline">{deal.owner.name.split(' ')[0]}</span>
      </div>
    ),
  },
  {
    key: 'value',
    header: 'Value',
    align: 'right',
    sortable: true,
    sortValue: (deal) => (deal.value != null ? Number(deal.value) : 0),
    render: (deal) => {
      const value = deal.value != null ? Number(deal.value) : 0;
      return <span className="num font-semibold text-secondary">{value > 0 ? money(value) : '—'}</span>;
    },
  },
  {
    key: 'stage',
    header: 'Stage',
    sortable: true,
    sortValue: (deal) => deal.stage.winProbability ?? 0,
    render: (deal) => {
      const probability = deal.stage.winProbability ?? 0;
      return (
        <div className="min-w-[8rem]">
          <div className="mb-1 flex items-center justify-between gap-2 text-caption">
            <span className="truncate text-grey-600">{deal.stage.name}</span>
            <span className="num text-grey-500">{probability}%</span>
          </div>
          <ProgressBar value={probability / 100} color="accent" size="sm" />
        </div>
      );
    },
  },
];

function UpcomingDealsTable({ deals }: { deals: Deal[] }) {
  return (
    <DataTable
      columns={UPCOMING_COLUMNS}
      data={deals}
      rowKey={(d) => d.id}
      rowHref={(d) => `/admin/deals/${d.id}`}
      rowLabel={(d) => d.title}
      density="dense"
      empty={<EmptyState title="Nothing due in the next 30 days" description="Deals with an expected close date land here." />}
    />
  );
}
